/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import Imgix from 'js/components/Imgix';

import { AlwaysLightMode, Link, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';

import { getPartnerPageUrl } from 'bundles/graphql/utils/Partners';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import type { PartnerListingPagePartnerFragment as PartnerListingPagePartner } from 'bundles/partners-graphql/queries/__generated__/GetAllPartners';

const DESKTOP_IMAGE_SIZE = 216;

const styles = {
  card: css`
    max-width: ${DESKTOP_IMAGE_SIZE}px;
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-border-radius-200);
    overflow: hidden;
    transition: box-shadow 0.2s ease;

    &:hover {
      box-shadow: 0 2px 8px rgb(0 0 0 / 8%);
    }

    ${breakpoints.down('sm')} {
      max-width: none;
    }
  `,
  link: css`
    text-decoration: none;
    color: inherit;

    &:hover {
      text-decoration: none;
    }
  `,
  desktopLayout: css`
    display: flex;
    flex-direction: column;
    padding: var(--cds-spacing-100);
    gap: var(--cds-spacing-100);
  `,
  mobileLayout: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--cds-spacing-100);
    padding: var(--cds-spacing-100);
  `,
  logoWrapper: css`
    border-radius: var(--cds-border-radius-100);
    overflow: hidden;
    background: var(--cds-color-neutral-background-primary);
  `,
  desktopImage: css`
    width: 100%;
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  `,
  mobileImage: css`
    width: 48px;
    height: 48px;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  `,
  desktopInfo: css`
    display: flex;
    flex-direction: column;
    gap: calc(var(--cds-spacing-50) / 2);
  `,
  mobileInfo: css`
    display: flex;
    flex-direction: column;
    gap: calc(var(--cds-spacing-50) / 2);
    min-width: 0;
  `,
  name: css`
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  `,
};

type Props = {
  partner: PartnerListingPagePartner;
  countryNamesMap?: Record<string, string>;
};

const PartnerBox = ({ partner, countryNamesMap = {} }: Props) => {
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const isExternal = partner.homePageOverride;
  const externalTarget = isExternal ? '_blank' : undefined;
  const externalRel = isExternal ? 'noopener noreferrer' : undefined;

  const countryCode = partner?.location?.country?.toUpperCase();
  const countryName = countryCode ? countryNamesMap[countryCode] : undefined;

  return (
    <div css={styles.card} className="rc-PartnerBox">
      <Link
        component={TrackedLink2}
        variant="standard"
        href={{ pathname: getPartnerPageUrl(partner) }}
        trackingName="partner_link"
        target={externalTarget}
        rel={externalRel}
        css={styles.link}
      >
        <div css={isMobile ? styles.mobileLayout : styles.desktopLayout}>
          <AlwaysLightMode>
            <div css={[styles.logoWrapper, isMobile ? styles.mobileImage : styles.desktopImage]}>
              <Imgix
                src={partner.squareLogo || ''}
                alt={`${partner.name} Logo`}
                maxWidth={DESKTOP_IMAGE_SIZE}
                maxHeight={DESKTOP_IMAGE_SIZE}
                imgParams={{ fit: 'crop' }}
              />
            </div>
          </AlwaysLightMode>
          <div css={isMobile ? styles.mobileInfo : styles.desktopInfo}>
            <Typography2 component="p" variant="subtitleMedium" css={styles.name}>
              {partner.name}
            </Typography2>
            {countryName && (
              <Typography2 component="p" variant="bodySecondary" color="supportText">
                {countryName}
              </Typography2>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
};

export default PartnerBox;
